import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  IconButton,
  useTheme,
  Divider,
  Chip,
  CircularProgress,
  useMediaQuery,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TokenIcon from '@mui/icons-material/Token';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentIcon from '@mui/icons-material/Payment';
import DoneIcon from '@mui/icons-material/Done';
import PaymentForm from './PaymentForm';
import { createPaymentIntent, processTokenPurchase } from '../services/paymentService';
import { useAuth } from '../contexts/AuthContext';

/**
 * TokenPurchaseDialog component displays purchase options when a user runs out of tokens
 */
const TokenPurchaseDialog = ({ open, onClose, onPurchase }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const [selectedOption, setSelectedOption] = useState(1); // Default to middle option
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState(null);
  const [purchaseComplete, setPurchaseComplete] = useState(false);

  // Token package options
  const tokenOptions = [
    { id: 0, tokens: 3, price: 5, label: 'Starter' },
    { id: 1, tokens: 8, price: 10, label: 'Value' },
    { id: 2, tokens: 20, price: 20, label: 'Deal' }
  ];

  // Calculate price per token
  const getPricePerToken = (tokens, price) => {
    return (price / tokens).toFixed(2);
  };

  // Steps for the purchase process
  const steps = ['Select Package', 'Payment Details', 'Confirmation'];

  // Handle moving to the payment step
  const handleProceedToPayment = async () => {
    try {
      // Check for secure context before proceeding to payment
      if (process.env.REACT_APP_NODE_ENV === 'production' && window.location.protocol !== 'https:') {
        setError('Payment processing requires a secure connection (HTTPS). Please contact support.');
        return;
      }
      
      setIsProcessing(true);
      setError(null);
      
      // Create a payment intent with Stripe
      const secret = await createPaymentIntent(tokenOptions[selectedOption]);
      
      setClientSecret(secret);
      
      // Move to the payment step
      setActiveStep(1);
    } catch (error) {
      console.error('Error creating payment intent:', error);
      setError('There was an error preparing your payment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle successful payment processing
  const handlePaymentSuccess = async (paymentDetails) => {
    try {
      setIsProcessing(true);
      setError(null);
      
      // Process the purchase on the backend
      const result = await processTokenPurchase(paymentDetails);
      
      // Move to confirmation step
      setActiveStep(2);
      setPurchaseComplete(true);
      
      // Call the onPurchase callback with the result
      setTimeout(() => {
        onPurchase(tokenOptions[selectedOption]);
      }, 2000);
      
    } catch (error) {
      console.error('Error processing payment:', error);
      setError('There was an error processing your payment. Your card may have been charged, but tokens were not added to your account. Please contact support.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle payment errors
  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setError(error.message || 'Payment failed. Please try again or use a different payment method.');
  };

  // Render content based on the current step
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <Typography variant="h5" gutterBottom fontWeight="bold" align="center" sx={{ 
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              mt: { xs: 2, sm: 3 }
            }}>
              Continue Your Travel Journey
            </Typography>

            <Typography variant="body1" align="center" sx={{ mb: 3, px: { xs: 1, sm: 3 } }}>
              Add more tokens to unlock personalized itineraries for your upcoming adventures.
            </Typography>


            <Grid container spacing={2} sx={{ mb: 4 }}>
              {tokenOptions.map((option) => (
                <Grid item xs={12} sm={4} key={option.id}>
                  <Card 
                    elevation={selectedOption === option.id ? 4 : 1}
                    onClick={() => !isProcessing && setSelectedOption(option.id)}
                    sx={{
                      cursor: isProcessing ? 'default' : 'pointer',
                      borderRadius: '10px',
                      position: 'relative',
                      height: '100%',
                      border: selectedOption === option.id ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: isProcessing ? 'none' : 'translateY(-4px)',
                        boxShadow: isProcessing ? 1 : 4
                      }
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: { xs: 2, sm: 3 } }}>
                      <Typography variant="h6" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {option.label}
                      </Typography>
                      
                      <Box sx={{ my: 1 }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.8rem', sm: '2.2rem' } }}>
                          ${option.price}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                        <TokenIcon color="primary" sx={{ mr: 0.5, fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                        <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                          {option.tokens} Tokens
                        </Typography>
                      </Box>
                      
                      <Divider sx={{ my: 1.5 }} />
                      
                      <Typography variant="body2" color="text.secondary">
                        ${getPricePerToken(option.tokens, option.price)} per token
                        {option.id === 1 && " (33% savings)"}
                        {option.id === 2 && " (67% savings)"}
                      </Typography>
                      
                      {selectedOption === option.id && (
                        <CheckCircleIcon 
                          color="primary"
                          sx={{ 
                            position: 'absolute',
                            bottom: 8,
                            right: 8,
                            fontSize: { xs: 20, sm: 24 }
                          }} 
                        />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {error && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Alert severity="error" sx={{ maxWidth: '80%' }}>
                  {error}
                </Alert>
              </Box>
            )}
            
            <Box sx={{ 
              bgcolor: 'rgba(94, 23, 235, 0.05)',
              p: { xs: 1.5, sm: 2 },
              borderRadius: '8px',
              mb: 1
            }}>
              <Typography variant="body2" align="center" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Tokens never expire! Each token creates a complete travel plan to help you explore the world your way.
              </Typography>
            </Box>
          </>
        );
      case 1:
        return (
          <Box sx={{ py: 2 }}>
            
            
            <Paper elevation={0} sx={{ 
              p: 2, 
              mb: 3, 
              bgcolor: 'rgba(94, 23, 235, 0.05)',
              borderRadius: '8px',
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" fontWeight="medium">
                  {tokenOptions[selectedOption].label} Package
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  ${tokenOptions[selectedOption].price}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TokenIcon fontSize="small" color="primary" sx={{ mr: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  {tokenOptions[selectedOption].tokens} tokens (${getPricePerToken(tokenOptions[selectedOption].tokens, tokenOptions[selectedOption].price)} each)
                </Typography>
              </Box>
            </Paper>
            
            {error && (
              <Box sx={{ display: 'flex', justifyContent: 'left', mb: 3 }}>
                <Alert severity="error" sx={{ maxWidth: '80%' }}>
                  {error}
                </Alert>
              </Box>
            )}
            
            {clientSecret && (
              <PaymentForm 
                clientSecret={clientSecret}
                packageDetails={{
                  ...tokenOptions[selectedOption],
                  userEmail: user?.email
                }}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
              />
            )}
          </Box>
        );
      case 2:
        return (
          <Box sx={{ 
            py: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Box sx={{ 
              bgcolor: 'rgba(76, 175, 80, 0.1)', 
              borderRadius: '50%',
              p: 2,
              mb: 2
            }}>
              <DoneIcon color="success" sx={{ fontSize: 60 }} />
            </Box>
            
            <Typography variant="h5" gutterBottom align="center" fontWeight="bold">
              Purchase Complete!
            </Typography>
            
            <Typography variant="body1" align="center" sx={{ mb: 3 }}>
              {tokenOptions[selectedOption].tokens} tokens have been added to your account.
            </Typography>
            
            <Typography variant="body2" color="text.secondary" align="center">
              You can now continue creating amazing travel itineraries.
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={(!isProcessing && !purchaseComplete) ? onClose : null}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #5E17EB 0%, #8C52FF 100%)',
        color: 'white',
        py: 2,
        position: 'relative'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TokenIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Purchase Tokens</Typography>
        </Box>
        {!isProcessing && !purchaseComplete && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white'
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{!isMobile ? label : null}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
        {renderStepContent()}
      </DialogContent>
      
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, justifyContent: 'center' }}>
        {activeStep === 0 && (
          <>
            <Button 
              onClick={onClose}
              disabled={isProcessing}
              sx={{ 
                minWidth: { xs: 80, sm: 100 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleProceedToPayment}
              disabled={isProcessing}
              startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <PaymentIcon />}
              sx={{ 
                minWidth: { xs: 140, sm: 160 },
                py: 1,
                px: { xs: 2, sm: 3 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              {isProcessing ? 'Processing...' : `Continue to Payment`}
            </Button>
          </>
        )}
        
        {activeStep === 1 && (
          <Button 
            onClick={() => setActiveStep(0)}
            disabled={isProcessing}
            sx={{ 
              minWidth: { xs: 80, sm: 100 },
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}
          >
            Back
          </Button>
        )}
        
        {activeStep === 2 && (
          <Button
            variant="contained"
            onClick={onClose}
            sx={{ 
              minWidth: { xs: 140, sm: 160 },
              py: 1,
              px: { xs: 2, sm: 3 },
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TokenPurchaseDialog; 